<template>
  <div class="page" ref="pageRef">
    <!-- Splash de espera 5s -->
    <div v-if="showSplash" class="splash">
      <img src="@assets/images/pantalla-carga-animada.svg" alt="Cargando resultados" />
      <div class="texto-cargando">{{ $t("components.chargerresultslistview.cargando_resultados") }}</div>
    </div>

    <div v-else class="layout">
      <div class="header-container">
        <!-- 1) Cabecera -->
        <header class="header">
          <div class="title-bar">
            <h1>{{ $t("components.chargerresultslistview.puntos_carga") }}</h1>
          </div>
          <div class="title-bar-buttons">
            <button class="title-bar-button" :aria-label="$t('components.chargerresultslistview.filtros')"
              @click="openFilters">
              <i class="fa-solid fa-sliders-up"></i>
              <span v-if="hasActiveFilters" class="filter-indicator"></span>
            </button>
          </div>
        </header>

        <!-- 2) Botonera de ordenación -->
        <nav class="sort-bar">
          <button v-for="option in sortOptions" :key="option.value"
            :class="['pill', { active: selectedSort === option.value }]" @click="handleSortChange(option.value)">{{
              option.label }}</button>
        </nav>


      </div>
      <!-- 3) Listado de resultados -->
      <main ref="resultsContainer" class="results" @scroll="handleScroll">
        <!-- Texto con la dirección del usuario -->
        <div class="location-text">
          Mostrando resultados cerca de {{ displayAddress }}
        </div>
        <!-- Mensaje de carga -->
        <div v-if="isLoadingResults && results.length === 0" class="loading-message">
          <div class="loading-spinner"></div>
          <p>{{ $t("components.chargerresultslistview.buscando_estaciones") }}</p>
        </div>

        <!-- Overlay de carga cuando hay resultados pero se está recargando (por ejemplo, cambio de ordenación) -->
        <div v-else-if="isLoadingResults && results.length > 0" class="loading-overlay">
          <img src="@assets/images/pantalla-carga-animada.svg" alt="Cargando resultados" />
          <div class="texto-cargando">{{ $t("components.chargerresultslistview.cargando_resultados") }}</div>
        </div>

        <!-- Mensaje de error -->
        <div v-else-if="apiError" class="error-message">
          <div class="error-icon">⚠️</div>
          <p>{{ apiError }}</p>
          <button @click="() => loadSearchResults()" class="retry-button">{{
            $t("components.chargerresultslistview.reintentar") }}</button>
        </div>

        <!-- Sin resultados -->
        <div v-else-if="results.length === 0" class="no-results">
          <div class="no-results-icon">🔍</div>
          <p>{{ $t("components.chargerresultslistview.no_encontraron_estaciones") }}</p>
          <p class="no-results-subtitle">{{ $t("components.chargerresultslistview.ampliar_radio") }}</p>
        </div>

        <!-- Lista de resultados -->
        <template v-else>
          <ChargerListItem v-for="item in results" :key="item.id" :name="item.name" :distanceKm="item.distanceKm"
            :priceKwh="item.priceKwh" :totalCost="item.totalCost" :powerKw="item.powerKw"
            :chargeTimeMin="item.chargeTimeMin" :latitud="item.latitud" :longitud="item.longitud" :Libre="item.libre"
            :Total="item.total" @navigation-error="handleNavigationError" />

          <!-- Indicador de carga de más resultados -->
          <div v-if="isLoadingMore" class="loading-more">
            <div class="loading-spinner"></div>
            <p>{{ $t("components.chargerresultslistview.cargando_mas_resultados") }}</p>
          </div>

          <!-- Mensaje cuando no hay más resultados -->
          <div v-else-if="!hasMoreResults && results.length > 0" class="no-more-results">
            <p>{{ $t("components.chargerresultslistview.no_mas_resultados") }}</p>
          </div>
        </template>
      </main>

      <!-- 4) Footer -->
      <ChargerResultsFooter activeTab="list" />
    </div>

    <!-- Modal de Filtros -->
    <ChargerListItemFilter v-if="showFilters" :activeFilters="activeFilters" @close="showFilters = false"
      @apply="handleApplyFilters" />

    <!-- Ventana flotante de error de navegación (cuando se intenta abrir mapas) -->
    <div v-if="navigationError" class="floating-error-panel">
      <button class="close-button" @click="navigationError = null">✕</button>
      <div class="error-content">
        <div class="error-icon">
          <i class="fa-solid fa-triangle-exclamation"></i>
        </div>
        <div class="error-text">
          <div class="error-title">{{ $t("components.chargerresultslistview.error_navegacion") }}</div>
          <div class="error-message">{{ navigationError }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, onActivated } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from 'vue-i18n';
import ChargerListItem from "@/components/charger/ChargerListItem.vue";
import ChargerResultsFooter from "@/components/share/ChargerResultsFooter.vue";
import ChargerListItemFilter from "@/components/charger/ChargerListItemFilter.vue";
import { useApi } from "@api/integration"
const router = useRouter();
const route = useRoute();
const api = useApi();
const { t } = useI18n();

// Referencia al contenedor de resultados para el scroll
const resultsContainer = ref<HTMLElement | null>(null);
const pageRef = ref<HTMLElement | null>(null);

// Datos de búsqueda desde localStorage
interface SearchData {
  vehicle: string;
  IdCocheElectrico: string;
  IdTarifaNombre: number | null;
  batteryCurrent: number;
  batteryTarget: number;
  BateriaInicial: number;
  BateriaFinal: number;
  location: { lat: number; lon: number } | null;
  address?: string;
}

const searchData = ref<SearchData | null>(null);
const userAddress = ref<string>("");

// Ubicación por defecto (Madrid)
const DEFAULT_LOCATION = {
  lat: 40.4169473,
  lon: -3.7035285,
  name: "Madrid, España"
};

// Dirección por defecto (Madrid)
const DEFAULT_ADDRESS = "Madrid, España";

// Dirección a mostrar (usuario o por defecto)
const displayAddress = computed(() => {
  return userAddress.value || DEFAULT_ADDRESS;
});

// Splash 5s
const showSplash = ref(true);
const isLoadingResults = ref(false);

// Modal de filtros
const showFilters = ref(false);

// Sort options - mapeo: valor local -> OrderBy del API (según CargadorOrderByEnum: "Distancia", "Precio", "Tiempo", "Potencia")
const sortOptions = [
  { label: t("components.chargerresultslistview.mas_cercanas"), value: "distance", orderBy: "Distancia" },
  { label: t("components.chargerresultslistview.mejor_precio"), value: "price", orderBy: "Precio" },
  { label: t("components.chargerresultslistview.menor_tiempo"), value: "time", orderBy: "Tiempo" },
  { label: t("components.chargerresultslistview.mayor_potencia"), value: "power", orderBy: "Potencia" },
] as const;
const selectedSort = ref<typeof sortOptions[number]["value"]>("distance");

// Resultados reales de la API
type ChargerItem = {
  id: string;
  name: string;
  distanceKm: number;
  priceKwh: number;
  totalCost: number;
  powerKw: number;
  chargeTimeMin: number;
  latitud: number;
  longitud: number;
  libre: number;
  total: number;
};

const results = ref<ChargerItem[]>([]);
const apiError = ref<string>("");
const navigationError = ref<string | null>(null); // Error al intentar abrir mapas

// Variables para el scroll infinito
const isLoadingMore = ref(false);
const hasMoreResults = ref(true);
const currentSkip = ref(0);
const TAKE_SIZE = 20; // Tamaño de página

// Filtros activos
interface ActiveFilters {
  chargerStatus: string;
  priceMax: number;
  distance: number;
  chargingTime: string;
}

const activeFilters = ref<ActiveFilters | null>(null);

// Computed para verificar si hay filtros activos
const hasActiveFilters = computed(() => {
  if (!activeFilters.value) return false;
  
  const filters = activeFilters.value;
  return (
    filters.chargerStatus === 'libre' ||
    filters.chargingTime !== 'any' ||
    (filters.priceMax > 0 && filters.priceMax !== 1) ||
    (filters.distance > 0 && filters.distance !== 10)
  );
});

function formatHoraCliente(d: Date) {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// Cargar datos de búsqueda y obtener resultados
async function loadSearchResults(orderBy?: string, skip: number = 0, append: boolean = false) {
  try {
    // Leer datos de búsqueda
    const storedData = localStorage.getItem('chargerSearchData');
    if (!storedData) {
      console.warn('No hay datos de búsqueda almacenados');
      return;
    }

    searchData.value = JSON.parse(storedData);

    // Cargar la dirección del usuario si existe
    if (searchData.value?.address) {
      userAddress.value = searchData.value.address;
    }

    // Si hay ubicación, buscar cargadores cercanos
    if (searchData.value?.location) {
      // Si es la primera carga (no append), mostrar loading principal
      if (!append) {
        isLoadingResults.value = true;
        currentSkip.value = 0;
      } else {
        isLoadingMore.value = true;
      }

      // Obtener OrderBy del sort seleccionado si no se proporciona o es inválido
      if (orderBy === undefined || orderBy === null || orderBy === '') {
        const currentSortOption = sortOptions.find(opt => opt.value === selectedSort.value);
        orderBy = currentSortOption?.orderBy || "Distancia"; // Por defecto: Distancia
      }

      // Si cambió el orden, resetear skip
      if (!append) {
        skip = 0;
        currentSkip.value = 0;
      }

      // Buscar cargadores con filtro por ubicación y ordenación
      const now = new Date();

      // Validar parámetros requeridos
      if (!searchData.value.location?.lat || !searchData.value.location?.lon) {
        throw new Error('La ubicación es requerida para buscar cargadores');
      }

      if (!searchData.value.IdCocheElectrico) {
        throw new Error('El vehículo es requerido para buscar cargadores');
      }

      // Construir queryParams filtrando valores null/undefined
      // Nota: Los nombres de parámetros deben coincidir exactamente con el swagger (PascalCase)
      // Asegurar que OrderBy siempre tenga un valor válido (es obligatorio en el endpoint)
      const queryParams: Record<string, any> = {
        Latitud: searchData.value.location.lat,
        Longitud: searchData.value.location.lon,
        Take: TAKE_SIZE,
        Skip: skip,
        OrderBy: orderBy || "Distancia", // Asegurar valor por defecto si es null/undefined/empty
        BateriaInicial: searchData.value.BateriaInicial ?? searchData.value.batteryCurrent,
        BateriaFinal: searchData.value.BateriaFinal ?? searchData.value.batteryTarget,
        IdCocheElectrico: searchData.value.IdCocheElectrico,
        HoraCliente: formatHoraCliente(now),
      };

      // Solo agregar IdTarifaNombre si tiene un valor válido (no null ni undefined)
      if (searchData.value.IdTarifaNombre != null) {
        queryParams.IdTarifaNombre = searchData.value.IdTarifaNombre;
      }

      // Aplicar filtros si están activos
      if (activeFilters.value) {
        // Precio máximo (según swagger: PrecioHasta)
        if (activeFilters.value.priceMax > 0) {
          queryParams.PrecioHasta = activeFilters.value.priceMax;
        }

        // Estado del cargador: SoloLibres = true si está seleccionado "Libre"
        // Si selecciona "Todos" no hacemos nada (no pasamos el parámetro)
        if (activeFilters.value.chargerStatus === 'libre') {
          queryParams.SoloLibres = true;
        }

        // Tiempo de carga máximo (según swagger: TiempoMaximo, en minutos)
        if (activeFilters.value.chargingTime !== 'any') {
          const timeMap: Record<string, number> = {
            '<15': 15,
            '<30': 30,
            '<45': 45,
            '<60': 60
          };
          const maxTimeMinutes = timeMap[activeFilters.value.chargingTime];
          if (maxTimeMinutes) {
            queryParams.TiempoMaximo = maxTimeMinutes;
          }
        }

        // Distancia máxima (según swagger: RadioMetros, en metros)
        // Convertimos de km (valor del filtro) a metros
        if (activeFilters.value.distance > 0) {
          queryParams.RadioMetros = activeFilters.value.distance * 1000; // km a metros
        }
      }

      // Por defecto, si no hay filtro de distancia, usar 10000 metros (10 km)
      if (!queryParams.RadioMetros) {
        queryParams.RadioMetros = 10000;
      }

      // Asegurar que OrderBy siempre tenga un valor válido (es obligatorio en el endpoint)
      // Validación final antes de la llamada a la API
      if (!queryParams.OrderBy || queryParams.OrderBy === '' || queryParams.OrderBy === null) {
        queryParams.OrderBy = "Distancia";
      }

      // Filtrar parámetros null/undefined antes de enviar (excepto OrderBy que es obligatorio)
      const cleanParams = Object.fromEntries(
        Object.entries(queryParams).filter(([_, value]) => value != null && value !== undefined && value !== '')
      );

      // Asegurar que OrderBy siempre tenga un valor válido antes de la llamada (es obligatorio)
      const orderByValue = queryParams.OrderBy || "Distancia";

      const cargadores = await api.callStandardApi(
        "getCargadorAll",
        { showLoading: false },
        queryParams["Take"],
        queryParams["Skip"],
        queryParams["BateriaInicial"],
        queryParams["BateriaFinal"],
        queryParams["IdCocheElectrico"],
        queryParams["IdTarifaNombre"],
        queryParams["Latitud"],
        queryParams["Longitud"],
        queryParams["HoraCliente"],
        orderByValue, // Siempre tiene un valor válido - OrderBy es requerido
        queryParams["RadioMetros"],
        queryParams["SoloLibres"],
        queryParams["PrecioHasta"],
        queryParams["TiempoMaximo"]
      );


      // Transformar datos de la API al formato esperado
      let nuevosCargadores = cargadores
        .filter((item: any) => item.Latitud != null && item.Longitud != null) // Solo cargadores con coordenadas
        .map((item: any) => {
          return {
            id: item.Id,
            name: item.Direccion,
            distanceKm: item.DistanciaM ? Math.round(item.DistanciaM / 1000 * 10) / 10 : 0, // convertir metros a km
            priceKwh: item.PrecioKwh || 0,
            totalCost: item.CosteTotal || 0,
            powerKw: item.Potencia || 0,
            chargeTimeMin: item.TiempoCarga || calculateChargeTime(item.Potencia, searchData.value?.batteryCurrent || 0, searchData.value?.batteryTarget || 0),
            latitud: item.Latitud,
            longitud: item.Longitud,
            libre: item.Libre ?? 0,
            total: item.Total ?? 0
          };
        });

      // Aplicar filtros del lado del cliente (para filtros que no soporta la API o como respaldo)
      if (activeFilters.value) {
        nuevosCargadores = nuevosCargadores.filter((item: ChargerItem) => {
          // Filtro por precio máximo
          if (activeFilters.value!.priceMax > 0 && item.priceKwh > activeFilters.value!.priceMax) {
            return false;
          }

          // Filtro por distancia (ya debería venir filtrado del servidor, pero lo verificamos)
          if (activeFilters.value!.distance > 0 && item.distanceKm > activeFilters.value!.distance) {
            return false;
          }

          // Filtro por tiempo de carga
          if (activeFilters.value!.chargingTime !== 'any') {
            const timeMap: Record<string, number> = {
              '<15': 15,
              '<30': 30,
              '<45': 45,
              '<60': 60
            };
            const maxTimeMinutes = timeMap[activeFilters.value!.chargingTime];
            if (maxTimeMinutes && item.chargeTimeMin > maxTimeMinutes) {
              return false;
            }
          }

          // Filtro por estado del cargador
          if (activeFilters.value!.chargerStatus === 'libre' && item.libre === 0) {
            return false;
          }

          return true;
        });
      }

      // Si hay menos resultados de los solicitados, no hay más resultados
      if (nuevosCargadores.length < TAKE_SIZE) {
        hasMoreResults.value = false;
      }

      // Añadir o reemplazar resultados según si es append
      if (append) {
        results.value = [...results.value, ...nuevosCargadores];
        currentSkip.value += nuevosCargadores.length;
      } else {
        results.value = nuevosCargadores;
        currentSkip.value = nuevosCargadores.length;
        hasMoreResults.value = nuevosCargadores.length === TAKE_SIZE; // Si obtuvo todos los solicitados, puede haber más
      }

    } else {
      console.warn('No hay ubicación disponible para buscar cargadores');
      apiError.value = t("components.chargerresultslistview.no_ubicacion_cargadores");
    }

  } catch (error: any) {
    console.error('Error cargando resultados:', error);

    // Mensajes de error más específicos
    if (error?.response?.status === 400) {
      const errorMessage = error?.response?.data?.message || error?.response?.data || error?.message || '';
      apiError.value = t("components.chargerresultslistview.error_solicitud", { message: errorMessage });
      console.error('Detalles del error 400:', {
        url: error?.config?.url,
        params: error?.config?.params,
        response: error?.response?.data
      });
    } else if (error?.response?.status === 401) {
      apiError.value = t("components.chargerresultslistview.no_autorizado");
    } else if (error?.response?.status === 500) {
      apiError.value = t("components.chargerresultslistview.error_servidor");
    } else if (error?.message) {
      apiError.value = error.message;
    } else {
      apiError.value = t("components.chargerresultslistview.error_cargar_resultados");
    }
  } finally {
    isLoadingResults.value = false;
    isLoadingMore.value = false;
  }
}

// Manejar el scroll para cargar más resultados
function handleScroll(event: Event) {
  const target = event.target as HTMLElement;
  if (!target || isLoadingMore.value || !hasMoreResults.value) {
    return;
  }

  // Calcular si estamos cerca del final (últimos 200px)
  const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

  if (scrollBottom < 200) {
    loadMoreResults();
  }
}

// Cargar más resultados
async function loadMoreResults() {
  if (isLoadingMore.value || !hasMoreResults.value) {
    return;
  }

  await loadSearchResults(undefined, currentSkip.value, true);
}

// Calcular coste total basado en la tarifa y diferencia de batería
function calculateTotalCost(pricePerKwh: number, currentPercent: number, targetPercent: number): number {
  if (!pricePerKwh) return 0;

  // Asumir una batería de 50kWh promedio (esto debería venir del vehículo seleccionado)
  const batteryCapacity = 50;
  const energyNeeded = (targetPercent - currentPercent) / 100 * batteryCapacity;

  return Math.round(energyNeeded * pricePerKwh * 100) / 100;
}

// Calcular tiempo de carga basado en la potencia del cargador
function calculateChargeTime(powerKw: number, currentPercent: number, targetPercent: number): number {
  if (!powerKw) return 0;

  // Asumir una batería de 50kWh promedio
  const batteryCapacity = 50;
  const energyNeeded = (targetPercent - currentPercent) / 100 * batteryCapacity;

  // Tiempo en minutos
  return Math.round((energyNeeded / powerKw) * 60);
}

// Función para manejar el cambio de ordenación
async function handleSortChange(sortValue: typeof sortOptions[number]["value"]) {
  selectedSort.value = sortValue;
  const sortOption = sortOptions.find(opt => opt.value === sortValue);
  if (sortOption) {
    // Resetear variables de paginación al cambiar orden
    hasMoreResults.value = true;
    currentSkip.value = 0;

    // Activar el estado de carga
    isLoadingResults.value = true;

    // Hacer scroll al inicio del contenedor antes de cargar
    if (resultsContainer.value) {
      resultsContainer.value.scrollTop = 0;
    }

    // Cargar resultados con la nueva ordenación
    await loadSearchResults(sortOption.orderBy, 0, false);

    // Asegurar que el scroll esté en el inicio después de cargar
    if (resultsContainer.value) {
      resultsContainer.value.scrollTop = 0;
    }
  }
}

// Watcher para recargar filtros cuando cambia la ruta (navegación entre vistas)
watch(() => route.path, async (newPath) => {
  if (newPath === '/charger-results') {
    // Recargar filtros desde localStorage al entrar en la vista de lista
    const previousFilters = activeFilters.value ? JSON.stringify(activeFilters.value) : null;
    loadFiltersFromStorage();
    const currentFilters = activeFilters.value ? JSON.stringify(activeFilters.value) : null;

    // Si los filtros cambiaron, recargar resultados
    if (previousFilters !== currentFilters) {
      hasMoreResults.value = true;
      currentSkip.value = 0;
      await loadSearchResults();
    }
  }
});

// Hook para cuando el componente se activa (si está dentro de keep-alive)
onActivated(async () => {
  // Recargar dirección, filtros y resultados cuando se activa el componente
  loadAddressFromStorage();
  loadFiltersFromStorage();
  hasMoreResults.value = true;
  currentSkip.value = 0;
  await loadSearchResults();

  // Actualizar ubicación GPS y dirección en segundo plano
  refreshUserLocation().catch(err => {
    console.warn('Error al refrescar ubicación:', err);
  });
});

onMounted(async () => {
  // Cargar dirección y filtros desde localStorage antes de cargar resultados
  loadAddressFromStorage();
  loadFiltersFromStorage();

  // Cargar resultados inmediatamente
  await loadSearchResults();

  // Ocultar splash una vez que terminó la carga
  showSplash.value = false;

  // Actualizar ubicación GPS y dirección en segundo plano (sin bloquear la UI)
  refreshUserLocation().catch(err => {
    console.warn('Error al refrescar ubicación:', err);
  });
});

function openFilters() {
  showFilters.value = true;
}

// Función para obtener la dirección desde coordenadas (geocodificación inversa)
async function getAddressFromCoordinates(lat: number, lon: number): Promise<string> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1&accept-language=es`,
      {
        headers: {
          'User-Agent': 'Endesa-Calculadora-Echarger/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Error en la geocodificación inversa');
    }

    const data = await response.json();

    if (data && data.address) {
      const address = data.address;
      const parts: string[] = [];

      // Nombre de calle
      if (address.road) {
        parts.push(address.road);
      } else if (address.street) {
        parts.push(address.street);
      }

      // Número
      if (address.house_number) {
        parts.push(address.house_number);
      }

      // Provincia
      if (address.state) {
        parts.push(address.state);
      } else if (address.province) {
        parts.push(address.province);
      }

      // Si no hay partes, usar el display_name como fallback
      if (parts.length === 0 && data.display_name) {
        return data.display_name;
      }

      return parts.join(', ');
    }

    return data.display_name || '';
  } catch (error) {
    console.warn('Error obteniendo dirección:', error);
    return '';
  }
}

// Función para obtener la ubicación del usuario y actualizar los datos
async function refreshUserLocation() {
  // Intentar con GPS primero
  if (!navigator.geolocation) {
    console.warn('Geolocalización GPS no disponible');
    return;
  }

  try {
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      let errorHandled = false;
      const timeoutDuration = 5000; // 5 segundos - reducido para fallar más rápido

      // Timeout de seguridad adicional para detectar problemas silenciosos
      const safetyTimeout = setTimeout(() => {
        if (!errorHandled) {
          console.warn('Timeout de seguridad: la geolocalización no respondió');
          errorHandled = true;
          const timeoutError = {
            code: 3, // TIMEOUT
            message: 'Tiempo de espera agotado al obtener la ubicación',
            PERMISSION_DENIED: 1,
            POSITION_UNAVAILABLE: 2,
            TIMEOUT: 3
          } as GeolocationPositionError;
          reject(timeoutError);
        }
      }, timeoutDuration);

      try {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (!errorHandled) {
              clearTimeout(safetyTimeout);
              errorHandled = true;
              resolve(pos);
            }
          },
          (error) => {
            if (!errorHandled) {
              clearTimeout(safetyTimeout);
              errorHandled = true;
              reject(error);
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 4000, // Reducido de 10s a 4s para fallar más rápido
            maximumAge: 0
          }
        );
      } catch (policyError: any) {
        // Capturar errores de política que pueden ocurrir antes del callback
        if (!errorHandled) {
          clearTimeout(safetyTimeout);
          errorHandled = true;
          console.warn('Violación de política de permisos detectada:', policyError);
          // Crear un objeto de error similar a GeolocationPositionError
          const geolocationError = {
            code: 1, // PERMISSION_DENIED
            message: policyError.message || 'Permisos de geolocalización bloqueados',
            PERMISSION_DENIED: 1,
            POSITION_UNAVAILABLE: 2,
            TIMEOUT: 3
          } as GeolocationPositionError;
          reject(geolocationError);
        }
      }
    });

    const newLocation = {
      lat: position.coords.latitude,
      lon: position.coords.longitude
    };

    // Actualizar localStorage con la nueva ubicación inmediatamente
    try {
      const storedData = localStorage.getItem('chargerSearchData');
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        parsedData.location = newLocation;
        localStorage.setItem('chargerSearchData', JSON.stringify(parsedData));

        // Si la ubicación cambió significativamente, recargar resultados
        if (searchData.value?.location) {
          const oldLat = searchData.value.location.lat;
          const oldLon = searchData.value.location.lon;
          const distance = Math.sqrt(
            Math.pow(newLocation.lat - oldLat, 2) + Math.pow(newLocation.lon - oldLon, 2)
          );
          // Si la distancia es mayor a ~1km (aproximadamente 0.009 grados), recargar
          if (distance > 0.009) {
            searchData.value.location = newLocation;
            await loadSearchResults();
          }
        }
      }
    } catch (error) {
      console.warn('Error actualizando ubicación en localStorage:', error);
    }

    // Obtener la dirección desde las coordenadas en segundo plano (no bloquea)
    getAddressFromCoordinates(
      position.coords.latitude,
      position.coords.longitude
    ).then(address => {
      if (address) {
        userAddress.value = address;
        // Actualizar la dirección en localStorage cuando esté disponible
        try {
          const storedData = localStorage.getItem('chargerSearchData');
          if (storedData) {
            const parsedData = JSON.parse(storedData);
            parsedData.address = address;
            localStorage.setItem('chargerSearchData', JSON.stringify(parsedData));
          }
        } catch (error) {
          console.warn('Error actualizando dirección en localStorage:', error);
        }
      }
    }).catch(error => {
      console.warn('Error obteniendo dirección:', error);
    });
  } catch (error: any) {
    console.warn('Error al obtener ubicación GPS:', error);
    // No mostramos error al usuario, simplemente usamos la ubicación guardada
  }
}

// Cargar dirección desde localStorage
function loadAddressFromStorage() {
  try {
    const storedData = localStorage.getItem('chargerSearchData');
    if (storedData) {
      const searchData = JSON.parse(storedData);
      if (searchData?.address) {
        userAddress.value = searchData.address;
      }
    }
  } catch (error) {
    console.warn('Error cargando dirección desde localStorage:', error);
  }
}

// Cargar filtros desde localStorage
function loadFiltersFromStorage() {
  try {
    const storedFilters = localStorage.getItem('chargerActiveFilters');
    if (storedFilters) {
      activeFilters.value = JSON.parse(storedFilters);
    }
  } catch (error) {
    console.warn('Error cargando filtros desde localStorage:', error);
  }
}

// Guardar filtros en localStorage
function saveFiltersToStorage(filters: ActiveFilters) {
  try {
    localStorage.setItem('chargerActiveFilters', JSON.stringify(filters));
  } catch (error) {
    console.warn('Error guardando filtros en localStorage:', error);
  }
}

// Manejar la aplicación de filtros
async function handleApplyFilters(filters: ActiveFilters) {
  activeFilters.value = filters;
  // Guardar filtros en localStorage para compartir entre vistas
  saveFiltersToStorage(filters);
  // Resetear paginación al aplicar filtros
  hasMoreResults.value = true;
  currentSkip.value = 0;

  // Activar el estado de carga
  isLoadingResults.value = true;

  // Hacer scroll al inicio del contenedor antes de cargar
  if (resultsContainer.value) {
    resultsContainer.value.scrollTop = 0;
  }

  // Cargar resultados con los nuevos filtros
  await loadSearchResults(undefined, 0, false);

  // Asegurar que el scroll esté en el inicio después de cargar
  if (resultsContainer.value) {
    resultsContainer.value.scrollTop = 0;
  }
}

// Manejar errores de navegación desde ChargerListItem
function handleNavigationError(error: string) {
  navigationError.value = error;
}
</script>

<style scoped lang="scss">
@import "@/styles/_vars.scss";
@import "@/styles/figma.scss";
@import "@/styles/_themevars.scss";

.page {
  background: var(--background-default-default);
  min-height: 100dvh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
}

.splash {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  flex-direction: column;

  .texto-cargando {
    color: var(--text-default-caption, #667790);
    font-size: var(--font-size-body-sm, 14px);
    font-weight: 400;
    line-height: var(--line-height-body-sm, 18px);
  }

}

.layout {
  display: grid;
  height: 100%;
  min-height: 100dvh;
  flex: 1;
  grid-template-rows: auto 1fr auto;

  .header-container {
    flex: 0 0 auto;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--background-default-default);
    padding: var(--spacing-sm, 8px) 0;

    .title-bar {
      padding-left: var(--spacing-md, 16px);
      display: flex;
      align-items: center;
      justify-content: start;
      gap: var(--spacing-xs, 8px);
    }

    .title-bar-buttons {
      display: flex;
      align-items: center;
      gap: 0;
    }

    .title-bar-button {
      background: transparent;
      border: 0;
      font-size: 24px;
      padding: var(--spacing-xs, 8px) var(--spacing-md, 16px);
      width: auto;
      display: flex;
      position: relative;

      i {
        color: var(--text-default-caption, #667790);
      }

      .filter-indicator {
        position: absolute;
        top: 4px;
        right: 12px;
        width: 12px;
        height: 12px;
        background-color: #1863ED;
        border-radius: 50%;
        border: 2px solid var(--background-default-default, #FFF);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
      }
    }

    h1 {
      color: var(--text-default-default, #000);
      font-size: var(--font-size-heading-h3, 18px);
      font-weight: 700;
      line-height: var(--line-height-heading-h3, 24px);
      margin: 0;
    }
  }

  main {
    overflow: auto;
    padding: var(--spacing-xs, 8px) var(--spacing-md, 16px) var(--spacing-lg, 24px);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md, 16px);
    min-height: 0;
  }

  :deep(.footer) {
    flex: 0 0 auto;
    margin-top: auto;
  }
}


.sort-bar {
  display: flex;
  gap: var(--spacing-xs, 8px);
  overflow-x: auto;
  max-width: 100vw;
  padding: 0 var(--spacing-md, 16px) var(--spacing-xs, 8px);
}

.location-text {
  color: var(--text-default-caption, #667790);
  font-size: var(--font-size-body-sm, 14px);
  font-weight: 400;
  line-height: var(--line-height-body-sm, 18px);
}

.pill {
  padding: var(--tag-vertical-padding) var(--tag-horizontal-padding, 16px);
  border-radius: var(--tag-radius, 16px);
  border: 1px solid var(--input-border, #8390A2);
  white-space: nowrap;
  background: transparent;

  &.active {
    border: 1px solid var(--border-primary-default, #D3135A);
    background: var(--background-primary-default, #D3135A);
    color: var(--text-default-inverse, #FFF);
  }
}

.results {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  position: relative;

  .loading-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-xl);
    text-align: center;
    flex: 1;

    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 4px solid var(--border-default-default, #C2CDDD);
      border-top: 4px solid var(--background-secondary-default, #1863ED);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: var(--spacing-md);
    }

    p {
      color: var(--text-default-caption, #667790);
      font-size: var(--font-size-body-sm, 14px);
    }
  }

  .error-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-xl);
    text-align: center;
    background: var(--background-normal-hover, #F0F0F0);
    border-radius: var(--card-border-radius, 16px);
    flex: 1;

    .error-icon {
      font-size: 48px;
      margin-bottom: var(--spacing-md);
    }

    p {
      color: var(--text-error-default, #DC2626);
      font-size: var(--font-size-body-sm, 14px);
      margin-bottom: var(--spacing-md);
    }

    .retry-button {
      background: var(--background-secondary-default, #1863ED);
      color: white;
      border: none;
      padding: var(--spacing-sm) var(--spacing-md);
      border-radius: var(--border-radius-default, 8px);
      font-size: var(--font-size-body-sm, 14px);
      cursor: pointer;

      &:hover {
        opacity: 0.9;
      }
    }
  }

  .no-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-xl);
    text-align: center;
    flex: 1;

    .no-results-icon {
      font-size: 48px;
      margin-bottom: var(--spacing-md);
    }

    p {
      color: var(--text-default-caption, #667790);
      font-size: var(--font-size-body-sm, 14px);
      margin-bottom: var(--spacing-xs);
    }

    .no-results-subtitle {
      font-size: var(--font-size-body-xs, 12px);
      color: var(--text-default-caption, #667790);
      opacity: 0.8;
    }
  }

  .loading-more {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-lg);
    text-align: center;

    .loading-spinner {
      width: 32px;
      height: 32px;
      border: 3px solid var(--border-default-default, #C2CDDD);
      border-top: 3px solid var(--background-secondary-default, #1863ED);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: var(--spacing-sm);
    }

    p {
      color: var(--text-default-caption, #667790);
      font-size: var(--font-size-body-sm, 14px);
    }
  }

  .no-more-results {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--spacing-md);
    text-align: center;

    p {
      color: var(--text-default-caption, #667790);
      font-size: var(--font-size-body-sm, 14px);
      opacity: 0.8;
    }
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--background-default-default, #FFF);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10;
    padding: var(--spacing-xl);
    text-align: center;

    img {
      margin-bottom: var(--spacing-md);
    }

    .texto-cargando {
      color: var(--text-default-caption, #667790);
      font-size: var(--font-size-body-sm, 14px);
      font-weight: 400;
      line-height: var(--line-height-body-sm, 18px);
    }
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

// Ventana flotante de error de navegación (similar a floating-panel)
.floating-error-panel {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--background-default-default, #FFF);
  border-radius: var(--card-border-radius, 16px);
  border: 1px solid var(--border-default-default, #C2CDDD);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  max-width: 400px;
  width: calc(100% - 40px);
  z-index: 1000;
  animation: slideUp 0.3s ease-out;
  padding: var(--spacing-md) var(--card-horizontal-padding, 16px);
}

@keyframes slideUp {
  from {
    transform: translateX(-50%) translateY(100%);
    opacity: 0;
  }

  to {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
}

.error-content {
  display: flex;
  gap: var(--spacing-md, 16px);
  align-items: flex-start;
}

.error-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-error-default, #EF4444);
  border-radius: 50%;
  color: white;
  font-size: 24px;
}

.error-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs, 8px);
}

.error-title {
  color: var(--text-primary-default, #D3135A);
  font-size: var(--font-size-body-lg, 18px);
  font-weight: 700;
  line-height: var(--line-height-body-md, 24px);
}

.error-message {
  color: var(--text-default-default, #000);
  font-size: var(--font-size-body-md, 16px);
  font-weight: 400;
  line-height: var(--line-height-body-md, 24px);
}

.close-button {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  color: #6b7280;
  z-index: 10;
  transition: all 0.2s;
}

.close-button:hover {
  background: #e5e7eb;
  color: #374151;
}
</style>
